import { Feature, RenderOn } from '@econsult/econsult-ui/dist/components/FlagControlledFeature/index';
import Button from 'components/Button';
import GenericModalTrigger from 'components/Modal/GenericModalTrigger';
import * as ModalStyle from 'components/Modal/styles';
import { convertHtmlStringToText } from 'components/RichTextEditor';
import { STRINGS } from 'config';
import { isToolbarPatient } from 'model/toolbar/patient';
import { convertVitalsToEntries, SnomedEntry } from 'model/toolbar/snomed';
import React from 'react';
import { writeToRecord } from 'store/action/toolbar/summariserWriteToRecord';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { mapSectionsForEmisSaveToRecord } from 'util/helper';
import CurrentPatient from './CurrentPatient';
import IsUsedWithToolbar from './Enabled';
import * as Style from './style';

interface SaveToRecordModalProps {
  summary: string;
  isModalOpenProp?: boolean;
  setShowModalFuncProp?: (arg: boolean) => void;
}

const SaveToRecordModal: React.FC<SaveToRecordModalProps> = ({ summary, isModalOpenProp, setShowModalFuncProp }) => {
  const dispatch = useAppDispatch();
  const {
    app: { clinicalSystemName },
    toolbar: { patient },
    snomed: { vitals, selectedSnomed, aiSnomedCodes, problemSnomed },
  } = useAppState();

  const vitalEntries: SnomedEntry[] = convertVitalsToEntries(vitals, clinicalSystemName);
  const selectedVitalEntries: SnomedEntry[] = vitalEntries.filter(entry => selectedSnomed.includes(entry.code));
  const selectedAiSnomedCodes: SnomedEntry[] =
    aiSnomedCodes?.filter(entry => selectedSnomed.includes(entry.code)) || [];
  const snomedCodes = [...selectedAiSnomedCodes, ...selectedVitalEntries];
  const handleConfirm = () => {
    if (isToolbarPatient(patient)) {
      const formattedSummary = convertHtmlStringToText(summary);
      const mappedSections = {
        ...mapSectionsForEmisSaveToRecord(formattedSummary),
        ...(problemSnomed ? { problem: problemSnomed } : null),
      };

      writeToRecord(dispatch, {
        patientId: patient.patientId,
        ...mappedSections,
        snomedCodes,
      });
    }
  };
  return (
    <Feature flags={[STRINGS.CUSTOMISATION.FEATURE_FLAGS.SUMMARISER_EMIS_INTEGRATION_ENABLED]}>
      <RenderOn>
        <IsUsedWithToolbar>
          <GenericModalTrigger
            trigger={<></>}
            isModalOpenProp={isModalOpenProp}
            title="Are you sure you want to save to this patient record?"
          >
            {() => {
              const closeModal = () => {
                if (setShowModalFuncProp) {
                  setShowModalFuncProp(false);
                }
              };
              const handleSaveToPatientRecord = () => {
                handleConfirm();
                closeModal();
              };
              return (
                <>
                  <Feature flags={[STRINGS.CUSTOMISATION.FEATURE_FLAGS.TEMP_SUMMARISER_SNOMED_ENABLED]}>
                    <RenderOn>
                      <Style.ModalText>
                        You are also saving <strong>{selectedSnomed.length}</strong> SNOMED code
                        {selectedSnomed.length === 1 ? '' : 's'} to the patient record. To change this, click “Return to
                        summary” and amend selected SNOMED codes.
                      </Style.ModalText>
                    </RenderOn>
                  </Feature>
                  <CurrentPatient />
                  <Style.WarningTextRed>
                    By choosing to save to their patient record, you are confirming the summary is correct.
                  </Style.WarningTextRed>
                  <ModalStyle.ButtonContainer>
                    <Button
                      id="Button-return to summary"
                      data-automation="Button-return to summary"
                      onClick={closeModal}
                      buttonText="Return to summary"
                      icon={null}
                      iconPosition="left"
                      state="secondary"
                      disabled={false}
                    />
                    <Button
                      id="ButtonSaveToPatientRecord"
                      data-automation="saveToPatientRecord"
                      onClick={handleSaveToPatientRecord}
                      buttonText="Save to patient record"
                      icon={null}
                      iconPosition="left"
                      state="primary"
                      disabled={!isToolbarPatient(patient)}
                    />
                  </ModalStyle.ButtonContainer>
                </>
              );
            }}
          </GenericModalTrigger>
        </IsUsedWithToolbar>
      </RenderOn>
    </Feature>
  );
};

export default SaveToRecordModal;
